import React, { useMemo, useState } from 'react';
import { UnitGroup, UnitDef } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';
import './Sections.css';

// Unit Master (multi-unit / UOM) — mirrors the Android UnitGroup + UnitGroupItem
// / SubUnit beans. A group holds a base unit (factor 1) and convertible
// sub-units, each with a factor expressed in the base unit (e.g. base Kg = 1,
// Gram = 0.001, Quintal = 100). Products may reference a group to bill in any of
// its units; the line price scales by the chosen unit's factor.
const blankRow = (): UnitDef => ({ name: '', factor: 1 });

const Units: React.FC = () => {
  const { unitGroups, products, addUnitGroup, updateUnitGroup, deleteUnitGroup } = usePos();
  const [editing, setEditing] = useState<UnitGroup | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [name, setName] = useState('');
  const [abbr, setAbbr] = useState('');
  const [units, setUnits] = useState<UnitDef[]>([blankRow()]);

  const countFor = (id: string) => products.filter((p) => p.unitGroupId === id).length;

  const sorted = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    return [...unitGroups]
      .filter((g) => !t || g.name.toLowerCase().includes(t))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [unitGroups, listSearch]);

  const reset = () => {
    setEditing(null);
    setName('');
    setAbbr('');
    setUnits([blankRow()]);
  };

  const setRow = (i: number, k: keyof UnitDef, v: string) =>
    setUnits((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: k === 'factor' ? parseFloat(v) || 0 : v } : r)));
  const addRow = () => setUnits((prev) => [...prev, blankRow()]);
  const delRow = (i: number) => setUnits((prev) => prev.filter((_, idx) => idx !== i));

  const buildData = (): Omit<UnitGroup, 'id'> => ({
    name: name.trim(),
    abbreviation: abbr.trim() || undefined,
    units: units.filter((u) => u.name.trim()).map((u) => ({ name: u.name.trim(), factor: u.factor || 1 })),
  });

  const save = () => {
    if (!name.trim()) return alert('Group name is required.');
    const data = buildData();
    if (data.units.length === 0) return alert('Add at least one unit.');
    if (editing) updateUnitGroup({ ...editing, ...data });
    else addUnitGroup(data);
    reset();
  };

  const edit = (g: UnitGroup) => {
    setEditing(g);
    setName(g.name);
    setAbbr(g.abbreviation ?? '');
    setUnits(g.units.length ? g.units.map((u) => ({ ...u })) : [blankRow()]);
  };

  const remove = () => {
    if (!editing) return;
    const inUse = countFor(editing.id);
    const msg = inUse
      ? `Delete "${editing.name}"? ${inUse} product(s) use it.`
      : 'Delete this unit group?';
    if (window.confirm(msg)) {
      deleteUnitGroup(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input
          className="pm-list-search"
          placeholder="Search Unit Group"
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
        />
        <div className="pm-list-scroll">
          {sorted.map((g) => (
            <button key={g.id} className={`pm-list-item ${editing?.id === g.id ? 'sel' : ''}`} onClick={() => edit(g)}>
              <span className="pm-li-name">
                {g.name}
                {g.abbreviation ? ` (${g.abbreviation})` : ''}
              </span>
              <span className="pm-li-meta">
                {g.units.map((u) => u.name).join(', ')} · {countFor(g.id)} product{countFor(g.id) === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {sorted.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No unit groups yet.
            </p>
          )}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? 'Edit Unit Group' : 'New Unit Group'}
          </span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Group Name *')}<input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div>{lbl('Abbreviation')}<input value={abbr} onChange={(e) => setAbbr(e.target.value)} placeholder="e.g. WT" /></div>
          </div>

          <div className="pf-section">Units (factor in base unit · first row is the base = 1)</div>
          <table className="sec-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Unit Name</th>
                <th style={{ width: 120 }}>Factor</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {units.map((u, i) => (
                <tr key={i}>
                  <td>
                    <input value={u.name} onChange={(e) => setRow(i, 'name', e.target.value)} placeholder={i === 0 ? 'Base unit (e.g. Kg)' : 'Sub-unit (e.g. Gram)'} />
                  </td>
                  <td>
                    <input type="number" value={u.factor} onChange={(e) => setRow(i, 'factor', e.target.value)} />
                  </td>
                  <td>
                    {units.length > 1 && (
                      <button className="row-del" onClick={() => delRow(i)} title="Remove">✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="pf-btn-orange" onClick={addRow} style={{ marginTop: 8 }}>+ Add Unit</button>
        </div>
      </div>
    </div>
  );
};

export default Units;
